import React, { useEffect, useRef, useState } from "react";
import { ButtonLoader4 } from "../../components/ButtonLoader";
import toast from "react-hot-toast";
import * as Yup from "yup";
import axios from "axios";
import { useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { getAccessToken } from "../../storage/storage";
import Swal from "sweetalert2";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";

const colors = [
  { name: "Pink", hex: "#FF69B4" },
  { name: "Blue", hex: "#0000FF" },
  { name: "Green", hex: "#008000" },
  { name: "Purple", hex: "#800080" },
  { name: "Orange", hex: "#FFA500" },
  { name: "Teal", hex: "#008080" },
  { name: "Red", hex: "#FF0000" },
  { name: "Yellow", hex: "#FFD700" },
];

export const ContactFormSchema = Yup.object({
  name: Yup.string().required("Enter property name")
  .max(255, "Property name must be 3-255 characters")
  .test("not-empty", "Property name cannot be only spaces", value => value && value.trim().length > 0)
  .matches(
    /^[A-Za-z0-9\s'-]+$/,
    "Property name can only contain letters, numbers, spaces, hyphens, and apostrophes"
  )
  .test("min-trimmed-length", "Property name must be at least 2 characters", value => value && value.trim().length >= 2),
  address_line1: Yup.string().required("Enter your address_line1"),
  // address_line2: Yup.string().required("Enter your address_line2"),
  city: Yup.string().required("Enter your city"),
  postal_code: Yup.string().required("Enter your postal_code"),
  country: Yup.string().required("Enter your country"),
  max_occupancy: Yup.string().required("Enter your max_occupancy"),
  num_bedrooms: Yup.string().required("Enter your num_bedrooms"),
  num_beds: Yup.string().required("Enter your num_beds"),
  num_bathrooms: Yup.string().required("Enter your num_bathrooms"),
  num_lounges: Yup.string().required("Enter your num_lounges"),
  property_size: Yup.string().required("Enter your property_size"),
  // access_instructions: Yup.string().required("Enter your access_instructions"),
  property_color: Yup.string().required("Enter your property_color"),
});

const PropertyScreen = () => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(false);
  const Navigate = useNavigate();
  const token = getAccessToken();
  const location = useLocation();
  const company = location.state?.company || null;
  const isEdit = sessionStorage.getItem("edit") === "true";
  const [address, setAddress] = useState(null);
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [sublocality, setSublocality] = useState("");
  const [county, setCounty] = useState("");

  const initialValues = {
    name: company?.name || "",
    address_line1: company?.address_line1 || "",
    address_line2: company?.address_line2 || "",
    city: company?.city || "",
    postal_code: company?.postal_code || "",
    country: company?.country || "",
    max_occupancy: company?.max_occupancy || "",
    num_bedrooms: company?.num_bedrooms || "",
    num_beds: company?.num_beds || "",
    num_bathrooms: company?.num_bathrooms || "",
    num_lounges: company?.num_lounges || "",
    property_size: company?.property_size || "",
    access_instructions: company?.access_instructions || "",
    property_color: company?.property_color || "",
  };

  const { values, errors, handleChange, handleSubmit, setFieldValue, touched } =
    useFormik({
      initialValues: initialValues,
      validationSchema: ContactFormSchema,
      onSubmit: async (values, action) => {
        setLoading(true);
        const apiUrl = isEdit
          ? `${BASE_URL}/api/properties/update/${company?.id}/`
          : `${BASE_URL}/api/properties/`;
        const method = isEdit ? "patch" : "post";

        await axios({
          method: method,
          url: apiUrl,
          data: { ...values, property_image: selectedFile },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        })
          .then((response) => {
            Swal.fire({
              icon: "success",
              title: response.data.message || "Data Added Successfully",
              timer: 2000,
              showConfirmButton: false,
              position: "center",
            });
            action.resetForm();
            sessionStorage.removeItem("edit");
            sessionStorage.setItem("PropertyData", "true");
            setSelectedFile(null);
            setLoading(false);
            Navigate("/settings/properties", { replace: true });
          })
          .catch((error) => {
            setLoading(false);
            setSelectedFile(null);
            Swal.fire({
              icon: "error",
              title: error.response?.data?.error || "Something went wrong!",
              timer: 2000,
              showConfirmButton: false,
              position: "center",
            });
            // toast.error(error.response?.data?.error || "Something went wrong");
          });
      },
    });

  const [formData, setFormData] = useState({
    propertyName: "",
    propertyImage: null,
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    country: "",
    accessInstructions: "",
    maxOccupancy: "",
    bedrooms: "",
    beds: "",
    bathrooms: "",
    lounges: "",
    currency: "GBP",
    propertySize: "",
    sizeUnit: "sq ft",
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file); // File ko state me save kar rahe hain
      // setFieldValue("property_image", file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setFormData({ ...formData, propertyPhoto: file });
    }
  };

  // const handleSelect = (selected) => {
  //   if (!selected) return;
  //   setAddress(selected);

  //   // Google Maps API Call
  //   fetch(
  //     `https://maps.googleapis.com/maps/api/geocode/json?place_id=${selected.value.place_id}&key=AIzaSyARW_FuFJKl3YM3brl9jhIONKKaojL6Wvw`
  //   )
  //     .then((response) => response.json())
  //     .then((data) => {
  //       if (data.results.length > 0) {
  //         const addressComponents = data.results[0].address_components;
  //         let cityName = "";
  //         let stateName = "";
  //         let countryName = "";
  //         let postalCodeValue = "";
  //         let sublocalityName = ""; // State to store "Stag Hill" or similar area name

  //         addressComponents.forEach((component) => {
  //           if (component.types.includes("locality")) {
  //             cityName = component.long_name;
  //           }
  //           if (
  //             component.types.includes("administrative_area_level_2") &&
  //             !cityName
  //           ) {
  //             cityName = component.long_name; // Backup city if locality is missing
  //           }
  //           if (component.types.includes("sublocality") && !cityName) {
  //             sublocalityName = component.long_name; // Capture sublocality like "Stag Hill"
  //           }
  //           if (component.types.includes("administrative_area_level_1")) {
  //             stateName = component.long_name;
  //           }
  //           if (component.types.includes("country")) {
  //             countryName = component.long_name;
  //           }
  //           if (component.types.includes("postal_code")) {
  //             postalCodeValue = component.long_name;
  //           }
  //         });

  //         // **Restrict Non-UK Addresses**
  //         if (countryName !== "United Kingdom") {
  //           Swal.fire({
  //             icon: "error",
  //             title: "Invalid Address",
  //             text: "Only UK addresses are allowed.",
  //           });
  //           return; // Stop execution
  //         }

  //         // Fallback for sublocality and state
  //         if (!sublocalityName) {
  //           // Use only sublocality, city, or state, but exclude postal code and country
  //           const formattedAddress = data.results[0]?.formatted_address || "";
  //           const filteredAddress = formattedAddress
  //             .split(", ")
  //             .filter((part) => {
  //               return (
  //                 !part.includes(postalCodeValue) && // Ignore postal code
  //                 !part.includes(countryName) // Ignore country name
  //               );
  //             })
  //             .join(", ");

  //           sublocalityName = filteredAddress;
  //         }

  //         if (!stateName) {
  //           stateName = cityName || sublocalityName || "Unknown"; // Ensure state is set, use city or sublocality if necessary
  //         }

  //         // Set the sublocality value to state (e.g., Stag Hill)
  //         setSublocality(sublocalityName); // New state for "Stag Hill"
  //         setCity(cityName);
  //         setCountry(countryName);
  //         setPostalCode(postalCodeValue);

  //         // Update Formik values
  //         setFieldValue("address_line1", sublocalityName);
  //         setFieldValue("city", cityName);
  //         setFieldValue("state", stateName); // Set state
  //         setFieldValue("country", countryName);
  //         setFieldValue("postal_code", postalCodeValue);
  //         // setFieldValue("address_line1", data.results[0].formatted_address);
  //       }
  //     })
  //     .catch((error) =>
  //       console.error("Error fetching address details:", error)
  //     );
  // };

  // const handleSelect = (selected) => {
  //   if (!selected) return;
  //   setAddress(selected);

  //   // Google Maps API Call
  //   fetch(
  //     `https://maps.googleapis.com/maps/api/geocode/json?place_id=${selected.value.place_id}&key=AIzaSyARW_FuFJKl3YM3brl9jhIONKKaojL6Wvw`
  //   )
  //     .then((response) => response.json())
  //     .then((data) => {
  //       if (data.results.length > 0) {
  //         const addressComponents = data.results[0].address_components;
  //         let cityName = "";
  //         let stateName = "";
  //         let countryName = "";
  //         let postalCodeValue = "";
  //         let sublocalityName = "";
  //         let countyName = ""; // New state for county

  //         addressComponents.forEach((component) => {
  //           if (component.types.includes("locality")) {
  //             cityName = component.long_name;
  //           }
  //           if (component.types.includes("postal_town") && !cityName) {
  //             cityName = component.long_name;
  //           }
  //           if (
  //             component.types.includes("administrative_area_level_2") &&
  //             !cityName
  //           ) {
  //             cityName = component.long_name; // Backup city
  //           }
  //           if (component.types.includes("sublocality") && !cityName) {
  //             sublocalityName = component.long_name;
  //           }
  //           if (component.types.includes("administrative_area_level_1")) {
  //             stateName = component.long_name;
  //           }
  //           if (component.types.includes("administrative_area_level_2")) {
  //             countyName = component.long_name; // Save county separately
  //           }
  //           if (component.types.includes("country")) {
  //             countryName = component.long_name;
  //           }
  //           if (component.types.includes("postal_code")) {
  //             postalCodeValue = component.long_name;
  //           }
  //         });

  //         // **Restrict Non-UK Addresses**
  //         if (countryName !== "United Kingdom") {
  //           Swal.fire({
  //             icon: "error",
  //             title: "Invalid Address",
  //             text: "Only UK addresses are allowed.",
  //           });
  //           return;
  //         }

  //         if (!sublocalityName) {
  //           const formattedAddress = data.results[0]?.formatted_address || "";
  //           const addressComponents = data.results[0]?.address_components || [];

  //           // Extract street name from "route" type
  //           let streetName =
  //             addressComponents.find((component) =>
  //               component.types.includes("route")
  //             )?.long_name || "";

  //           sublocalityName = streetName;
  //         }

  //         if (!stateName) {
  //           stateName = cityName || sublocalityName || "Unknown";
  //         }

  //         // Set the new county state
  //         setCounty(countyName);
  //         setSublocality(sublocalityName);
  //         setCity(cityName);
  //         setCountry(countryName);
  //         setPostalCode(postalCodeValue);

  //         // Update Formik values
  //         setFieldValue("address_line1", sublocalityName);
  //         setFieldValue("city", cityName);
  //         setFieldValue("state", stateName);
  //         setFieldValue("county", countyName); // New county field
  //         setFieldValue("country", countryName);
  //         setFieldValue("postal_code", postalCodeValue);
  //       }
  //     })
  //     .catch((error) =>
  //       console.error("Error fetching address details:", error)
  //     );
  // };

  const handleSelect = (selected) => {
    if (!selected) return;
    setAddress(selected);

    // Google Maps API Call
    fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?place_id=${selected.value.place_id}&key=AIzaSyARW_FuFJKl3YM3brl9jhIONKKaojL6Wvw`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.results.length > 0) {
          const addressComponents = data.results[0].address_components;
          let cityName = "";
          let stateName = "";
          let countryName = "";
          let postalCodeValue = "";
          let sublocalityName = "";
          let countyName = ""; // New state for county

          addressComponents.forEach((component) => {
            if (component.types.includes("locality")) {
              cityName = component.long_name;
            }
            if (component.types.includes("postal_town") && !cityName) {
              cityName = component.long_name;
            }
            if (
              component.types.includes("administrative_area_level_2") &&
              !cityName
            ) {
              cityName = component.long_name; // Backup city
            }
            if (component.types.includes("sublocality") && !cityName) {
              sublocalityName = component.long_name;
            }
            if (component.types.includes("administrative_area_level_1")) {
              stateName = component.long_name;
            }
            if (component.types.includes("administrative_area_level_2")) {
              countyName = component.long_name; // Save county separately
            }
            if (component.types.includes("country")) {
              countryName = component.long_name;
            }
            if (component.types.includes("postal_code")) {
              postalCodeValue = component.long_name;
            }
          });

          // **Restrict Non-UK Addresses**
          if (countryName !== "United Kingdom") {
            Swal.fire({
              icon: "error",
              title: "Invalid Address",
              text: "Only UK addresses are allowed.",
            });
            return;
          }

          // **Check if sublocalityName is empty, then use first part of formatted_address**
          if (!sublocalityName) {
            const formattedAddress = data.results[0]?.formatted_address || "";
            sublocalityName = formattedAddress.split(",")[0]; // Pehla part lelo
          }

          if (!stateName) {
            stateName = cityName || sublocalityName || "Unknown";
          }

          // Set the new county state
          setCounty(countyName);
          setSublocality(sublocalityName);
          setCity(cityName);
          setCountry(countryName);
          setPostalCode(postalCodeValue);

          // Update Formik values
          setFieldValue("address_line1", sublocalityName);
          setFieldValue("city", cityName);
          setFieldValue("state", stateName);
          setFieldValue("county", countyName);
          setFieldValue("country", countryName);
          setFieldValue("postal_code", postalCodeValue);
        }
      })
      .catch((error) =>
        console.error("Error fetching address details:", error)
      );
  };

  useEffect(() => {
    if (company?.property_size) {
      // If the property_size comes from the API, split the value and the unit
      const [sizeValue, sizeUnit] = company?.property_size.split(" ");

      // Set the appropriate values in form state
      setFieldValue("property_size_value", sizeValue);
      setFieldValue("property_size_unit", sizeUnit || "sq ft");
    }
  }, [company?.property_size, setFieldValue]);

  return (
    <div className="pb-10">
      <div className="flex flex-col items-center justify-center mt-6 max-w-[800px] mx-auto">
        <img className="w-20 pb-3" src="/images/property.png" alt="" />
        <h1 className="md:text-3xl text-lg font-bold text-[#586989] mb-4">
          Property Detail
        </h1>
        <p className="text-gray-600 mb-6 text-center">
          Please enter the information about your Short Term Rental property
          here{" "}
        </p>
        <div className=" bg-[#FFF5CE] p-4 rounded-lg w-full">
          <h3 className="text-gray-700 font-semibold mb-2 flex items-center">
            {/* Property Color <span className="ml-1 text-green-500">&#9432;</span> */}
            Property Color*
          </h3>
          <div className="grid grid-cols-4 gap-4 items-center justify-center">
            {colors.map((color) => (
              <label
                key={color.name}
                className="flex items-center cursor-pointer group transition-all duration-300"
              >
                <input
                  type="radio"
                  name="property_color"
                  value={color.name}
                  checked={values.property_color === color.name}
                  onChange={() => {
                    setFieldValue("property_color", color.name);
                  }}
                  className="hidden"
                />
                <span
                  className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300
        ${
          values.property_color === color.name
            ? "border-black scale-110 shadow-md shadow-gray-400"
            : "border-gray-300 opacity-75"
        }`}
                  style={{ backgroundColor: color.hex }}
                ></span>
                <span
                  className={`ml-3 text-gray-700 text-sm font-semibold transition-all duration-300 ${
                    values.property_color === color.name
                      ? "text-black"
                      : "text-gray-500"
                  }`}
                >
                  {color.name}
                </span>
              </label>
            ))}
          </div>
          {errors.property_color && touched.property_color && (
            <p className="text-[#DC2626] text-[12px] h-[6px] pb-2 pt-2">
              {errors.property_color || ""}
            </p>
          )}
        </div>
      </div>
      <div className="container max-w-[800px] mx-auto p-4 bg-[#FFF5CE] pb-4">
        <h2 className="text-xl font-bold">
          Basic Property Registration Screen
        </h2>
        <form className="space-y-6 mt-4">
          <div className="flex flex-col gap-2">
            <label>Property Name*</label>
            <input
              type="text"
              name="name"
              // placeholder="name*"
              value={values.name}
              onChange={handleChange}
              required
              className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
            />
            {errors.name && touched.name && (
              <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                {errors.name || ""}
              </p>
            )}
          </div>
          <div>
            {/* File Upload Box */}
            <label>Property Image</label>
            <div
              className="bg-white border-dashed border-[#586989] border-2 p-6 text-center mt-4 cursor-pointer h-[150px] flex flex-col items-center justify-center rounded-lg"
              onClick={() => fileInputRef.current.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <p className="text-gray-700">Click to upload or drag and drop</p>
            </div>
            {selectedFile ? (
              <p className="text-sm text-green-600 mt-2">{selectedFile.name}</p>
            ) : company?.property_image ? (
              <img
                src={company.property_image}
                alt="Property"
                className="mt-2 w-full h-[120px] object-cover rounded-lg"
              />
            ) : (
              <></>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label>Search Address</label>
            <GooglePlacesAutocomplete
              apiKey="AIzaSyARW_FuFJKl3YM3brl9jhIONKKaojL6Wvw"
              selectProps={{
                address,
                onChange: handleSelect,
                placeholder: "Enter UK Address",
              }}
              autocompletionRequest={{
                componentRestrictions: { country: "GB" },
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label>Address Line 1*</label>

            <input
              type="text"
              name="address_line1"
              // placeholder="address_lin1"
              value={sublocality || company?.address_line1}
              onChange={(e) => {
                handleChange(e);
                setSublocality(e.target.value);
              }}
              required
              className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
            />
            {errors.address_line1 && touched.address_line1 && (
              <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                {errors.address_line1 || ""}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label>Address Line 2</label>
            <input
              type="text"
              name="address_line2"
              // placeholder="address_line2"
              value={values.address_line2}
              onChange={handleChange}
              className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 w-full">
            <div className="flex flex-col gap-2 w-full">
              <label>City/Town*</label>
              <input
                type="text"
                name="city"
                // placeholder="city*"
                value={city || company?.city}
                onChange={(e) => {
                  handleChange(e);
                  setCity(e.target.value);
                }}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              />
              {errors.city && touched.city && (
                <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                  {errors.city || ""}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 w-full">
              <label>County*</label>
              <input
                type="text"
                name="county"
                value={county}
                // placeholder="County*"
                onChange={(e) => {
                  handleChange(e);
                  setCounty(e.target.value);
                }}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              />
            </div>
            <div className="flex flex-col gap-2 w-full">
              <label>Country*</label>
              <input
                type="text"
                name="country"
                value={country || company?.country}
                onChange={(e) => {
                  handleChange(e);
                  setCountry(e.target.value);
                }}
                // placeholder="country*"
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              />
              {errors.country && touched.country && (
                <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                  {errors.country || ""}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 w-full">
              <label>Post Code*</label>
              <input
                type="text"
                name="postal_code"
                // placeholder="Post Code*"
                value={postalCode || company?.postal_code}
                onChange={(e) => {
                  handleChange(e);
                  setPostalCode(e.target.value);
                }}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              />
              {errors.postal_code && touched.postal_code && (
                <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                  {errors.postal_code || ""}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label>Access Instructions</label>
            <textarea
              name="access_instructions"
              // placeholder="access_instructions"
              value={values.access_instructions}
              onChange={handleChange}
              required
              className="w-full p-2 border outline-none h-[120px] resize-none px-4 rounded-lg"
            ></textarea>
            {errors.access_instructions && touched.access_instructions && (
              <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                {errors.access_instructions || ""}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 w-full">
            <label>Online Travel Agent (OTA) Link</label>
            <input
              type="text"
              name="ota"
              // placeholder="online travel agent (OTA) link"
              // value={formData.maxOccupancy}
              // onChange={handleChange}
              required
              className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex flex-col gap-2">
              <label>Maximum Occupancy*</label>
              {/* <input
                type="number"
                name="max_occupancy"
                placeholder="Max Occupancy*"
                value={values.max_occupancy}
                onChange={handleChange}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              /> */}
              <select
                name="max_occupancy"
                value={values.max_occupancy}
                onChange={handleChange}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              >
                <option value="" disabled>
                  Max Occupancy*
                </option>
                {[...Array(20)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              {errors.max_occupancy && touched.max_occupancy && (
                <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                  {errors.max_occupancy || ""}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label>Number of Bedrooms*</label>
              {/* <input
                type="number"
                name="num_bedrooms"
                placeholder="Number of Bedrooms*"
                value={values.num_bedrooms}
                onChange={handleChange}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              /> */}

              <select
                name="num_bedrooms"
                value={values.num_bedrooms}
                onChange={handleChange}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              >
                <option value="" disabled>
                  num bedrooms*
                </option>
                {[...Array(99)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              {errors.num_bedrooms && touched.num_bedrooms && (
                <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                  {errors.num_bedrooms || ""}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label>Number of Beds*</label>
              {/* <input
                type="number"
                name="num_beds"
                placeholder="Number of Beds*"
                value={values.num_beds}
                onChange={handleChange}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              /> */}
              <select
                name="num_beds"
                value={values.num_beds}
                onChange={handleChange}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              >
                <option value="" disabled>
                  num beds*
                </option>
                {[...Array(99)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              {errors.num_beds && touched.num_beds && (
                <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                  {errors.num_beds || ""}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label>Number of bathrooms*</label>
              {/* <input
                type="number"
                name="num_bathrooms"
                placeholder="Number of Bathrooms*"
                value={values.num_bathrooms}
                onChange={handleChange}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              /> */}

              <select
                name="num_bathrooms"
                value={values.num_bathrooms}
                onChange={handleChange}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              >
                <option value="" disabled>
                  num bathrooms*
                </option>
                {[...Array(99)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              {errors.num_bathrooms && touched.num_bathrooms && (
                <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                  {errors.num_bathrooms || ""}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label>Number of Lounges*</label>
              {/* <input
                type="number"
                name="num_lounges"
                placeholder="Number of Lounges*"
                value={values.num_lounges}
                onChange={handleChange}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              /> */}
              <select
                name="num_lounges"
                value={values.num_lounges}
                onChange={handleChange}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              >
                <option value="" disabled>
                  num lounges*
                </option>
                {[...Array(25)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              {errors.num_lounges && touched.num_lounges && (
                <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                  {errors.num_lounges || ""}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label> Property Currency*</label>
              <select
                name="currency"
                value={values.currency}
                onChange={handleChange}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              >
                <option value="GBP">GBP (£)</option>
                <option value="USD">USD ($)</option>
              </select>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label>Property Size</label>
            <div className="flex md:flex-row flex-col gap-4">
              {/* <input
                type="number"
                name="property_size_value"
                placeholder="Property Size*"
                value={values.property_size_value || ""}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFieldValue("property_size_value", newValue);
                  setFieldValue(
                    "property_size",
                    `${newValue} ${values.property_size_unit || "sq ft"}`
                  );
                }}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              /> */}
              <input
                type="number"
                name="property_size_value"
                // placeholder="Property Size*"
                value={values.property_size_value || ""}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFieldValue("property_size_value", newValue);
                  setFieldValue(
                    "property_size",
                    `${newValue} ${values.property_size_unit || "sq ft"}`
                  );
                }}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              />
              <select
                name="property_size_unit"
                value={values.property_size_unit || "sq ft"}
                onChange={(e) => {
                  const newUnit = e.target.value;
                  setFieldValue("property_size_unit", newUnit);
                  setFieldValue(
                    "property_size",
                    `${values.property_size_value || 0} ${newUnit}`
                  );
                }}
                required
                className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
              >
                <option value="sq ft">sq ft</option>
                <option value="sq m">sq m</option>
              </select>
            </div>
            {errors.property_size && touched.property_size && (
              <p className="text-[#DC2626] text-[12px] h-[6px] pb-2">
                {errors.property_size || ""}
              </p>
            )}
          </div>

          {/* button */}
          <div className="w-full flex  justify-end">
            {isEdit ? (
              <button
                type="submit"
                onClick={handleSubmit}
                className="bg-[#586989] text-white w-[100px] h-[40px] rounded-md"
              >
                {loading ? <ButtonLoader4 /> : "Update"}
              </button>
            ) : (
              <button
                type="submit"
                onClick={handleSubmit}
                className="bg-[#586989] text-white w-[100px] h-[40px] rounded-md"
              >
                {loading ? <ButtonLoader4 /> : "Submit"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyScreen;

// import React, { useState } from "react";
// import GooglePlacesAutocomplete from "react-google-places-autocomplete";

// const AddressAutoComplete = () => {
//   const [address, setAddress] = useState(null);
//   const [city, setCity] = useState("");
//   const [country, setCountry] = useState("");

//   const handleSelect = (selected) => {
//     setAddress(selected);

//     // Extract city & country
//     fetch(
//       `https://maps.googleapis.com/maps/api/geocode/json?place_id=${selected?.value?.place_id}&key=AIzaSyARW_FuFJKl3YM3brl9jhIONKKaojL6Wvw`
//     )
//       .then((response) => response.json())
//       .then((data) => {
//         if (data.results.length > 0) {
//           const addressComponents = data?.results[0].address_components;

//           let cityName = "";
//           let countryName = "";

//           addressComponents.forEach((component) => {
//             if (component.types.includes("locality")) {
//               cityName = component.long_name;
//             }
//             if (component.types.includes("country")) {
//               countryName = component.long_name;
//             }
//           });

//           setCity(cityName);
//           setCountry(countryName);
//         }
//       })
//       .catch((error) =>
//         console.error("Error fetching address details:", error)
//       );
//   };

//   return (
//     <div>
//       <label>Address:</label>
//       <GooglePlacesAutocomplete
//         selectProps={{
//           value: address,
//           onChange: handleSelect,
//         }}
//       />

//       <div>
//         <label>City:</label>
//         <input type="text" value={city} readOnly />
//       </div>

//       <div>
//         <label>Country:</label>
//         <input type="text" value={country} readOnly />
//       </div>
//     </div>
//   );
// };

// export default AddressAutoComplete;
